import { z } from 'zod';
import { compareDatesNullsLast } from './dates';
import { ProjectStatuses } from './enums';
import { dateSchema, optionalDate, optionalHours, optionalId, requiredText, versionField } from './fields';
import { compareText } from './format';
import { idSchema } from './ids';

/** FR-TASK-001/016: one task form everywhere (project card, tasks page, customer card). */
export const taskInputSchema = z.object({
  projectId: z.string().min(1, { message: 'Izvēlies projektu!' }).pipe(idSchema),
  title: requiredText(500, 'Ievadi uzdevuma aprakstu!'),
  /** Responsible resource (PMAI stored a name; here a reference). */
  assigneeId: optionalId,
  estimatedHours: optionalHours,
  actualHours: optionalHours,
  deadline: optionalDate,
  isPriority: z.boolean().default(false),
  done: z.boolean().default(false),
});
export type TaskInput = z.input<typeof taskInputSchema>;

export const taskUpdateSchema = taskInputSchema.extend(versionField);
export const taskDoneSchema = z.object({ done: z.boolean() });

export const taskSchema = z.object({
  id: idSchema,
  projectId: idSchema,
  projectName: z.string(),
  projectStatus: ProjectStatuses.schema,
  customerId: idSchema,
  customerName: z.string(),
  title: z.string(),
  assigneeId: idSchema.nullable(),
  assigneeName: z.string().nullable(),
  estimatedHours: z.number().nullable(),
  actualHours: z.number().nullable(),
  deadline: dateSchema.nullable(),
  isPriority: z.boolean(),
  done: z.boolean(),
  doneAt: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Task = z.infer<typeof taskSchema>;

export const TASK_STATUS_FILTERS = ['all', 'open', 'done'] as const;
export type TaskStatusFilter = (typeof TASK_STATUS_FILTERS)[number];

/**
 * One task list endpoint for every view: a project's or customer's tasks, or all tasks by
 * deadline period (FR-TASK-011: with a period, tasks without a deadline are not listed).
 */
export const taskListQuerySchema = z.object({
  projectId: idSchema.optional(),
  customerId: idSchema.optional(),
  assigneeId: idSchema.optional(),
  status: z.enum(TASK_STATUS_FILTERS).default('all'),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  /** `board` — FR-TASK-004 (cards); `deadline` — FR-TASK-015 (tasks page). */
  order: z.enum(['board', 'deadline']).default('board'),
});
export type TaskListQuery = z.infer<typeof taskListQuerySchema>;

export const taskSummarySchema = z.object({
  count: z.number().int(),
  done: z.number().int(),
  estimatedHours: z.number(),
  actualHours: z.number(),
});
export type TaskSummary = z.infer<typeof taskSummarySchema>;

export const taskListSchema = z.object({ rows: z.array(taskSchema), summary: taskSummarySchema });
export type TaskList = z.infer<typeof taskListSchema>;

/** FR-TASK-004 (+ FR-CUST-013): open first, priority first, deadline ↑ (none last), newest first, project. */
export function compareTasksBoard(a: Task, b: Task): number {
  return (
    Number(a.done) - Number(b.done) ||
    Number(b.isPriority) - Number(a.isPriority) ||
    compareDatesNullsLast(a.deadline, b.deadline) ||
    (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0) ||
    compareText(a.projectName, b.projectName)
  );
}

/** FR-TASK-015: deadline ↑ (none last), then project name, then title. */
export function compareTasksByDeadline(a: Task, b: Task): number {
  return (
    compareDatesNullsLast(a.deadline, b.deadline) ||
    compareText(a.projectName, b.projectName) ||
    compareText(a.title, b.title)
  );
}

/** FR-TASK-008: an open task whose deadline is before today. */
export const isTaskOverdue = (task: Pick<Task, 'done' | 'deadline'>, today: string): boolean =>
  !task.done && task.deadline !== null && task.deadline < today;

/** FR-TASK-003: totals over the listed tasks. */
export function summarizeTasks(rows: readonly Task[]): TaskSummary {
  return {
    count: rows.length,
    done: rows.filter((t) => t.done).length,
    estimatedHours: rows.reduce((s, t) => s + (t.estimatedHours ?? 0), 0),
    actualHours: rows.reduce((s, t) => s + (t.actualHours ?? 0), 0),
  };
}
